import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  Input,
  output,
  signal,
  ViewChild,
  ViewEncapsulation,
} from '@angular/core';
import { MatButton } from '@angular/material/button';
import { MatTooltip } from '@angular/material/tooltip';
import { createTimeline } from 'animejs';
import { ViewMode } from './view-mode.interface';

@Component({
  selector: 'fz-view-mode',
  standalone: true,
  imports: [MatButton, MatTooltip],
  templateUrl: './view-mode.component.html',
  styleUrl: './view-mode.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
  host: {
    class: 'fz-view-mode',
    '[class.fz-view-mode--grid]': `viewMode() === 'grid'`,
    '[class.fz-view-mode--list]': `viewMode() === 'list'`,
  },
})
export class ViewModeComponent {
  @ViewChild('indRef') indRef!: ElementRef;

  /**
   * Initial view mode
   */
  @Input() set initialMode(value: ViewMode | undefined) {
    this.viewMode.set(value ?? ViewMode.cards);
  }

  /**
   * Emitted when the view mode changes
   */
  viewModeChange = output<ViewMode>();

  /**
   * Current view mode
   */
  viewMode = signal(ViewMode.cards);

  ViewMode = ViewMode;

  private animateIndicator(mode: ViewMode) {
    const el = this.indRef.nativeElement;
    const t = createTimeline();
    const initialLeft = mode === ViewMode.List ? 48 : 0;
    const midLeft = mode === ViewMode.List ? 24 : 0;
    const finalLeft = mode === ViewMode.List ? 0 : 48;

    t.add(el, {
      width: ['50%', '75%'],
      left: [initialLeft, midLeft],
      duration: 240,
      easing: 'easeOutQuad',
    }).add(
      el,
      {
        width: ['75%', '50%'],
        left: [midLeft, finalLeft],
        duration: 160,
        easing: 'easeInOutQuad',
      },
      '-=50',
    );
  }

  /**
   * Toggles the view mode between list and grid and emits the change.
   */
  toggleViewMode(mode: ViewMode) {
    this.viewMode.set(mode);
    this.viewModeChange.emit(this.viewMode());

    this.animateIndicator(mode);
  }
}
